import React from 'react';

const TermsModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const clinicName = "South Vancouver Medical Clinic";
  const websiteUrl = "DriverMedicalExam.com";
  const serviceEmail = "info@southvanmed.com";
  const themeColor = "#0ea5e9"; // sky-500

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-75"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className="bg-gray-50 px-6 py-4 border-b-2" style={{ borderColor: themeColor }}>
            <div className="text-center">
              <div className="text-sm text-gray-600 mb-1">{websiteUrl}</div>
              <h2 className="text-2xl font-semibold text-gray-900">TERMS OF USE</h2>
              <div className="text-sm text-gray-500 mt-2">Version 3.0 | Last Updated: January 20, 2026</div>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-6 max-h-[60vh] overflow-y-auto text-gray-700 leading-relaxed">
            <p className="mb-6">
              Please read this document completely. Continuing onto <strong>{websiteUrl}</strong> means that you agree with these Terms of Use.
            </p>

            <p className="mb-6">
              <strong>{clinicName}</strong> ("{clinicName}", "we", "us", or "our") owns and operates the website <strong>{websiteUrl}</strong>, 
              including any subdomains thereof (the "Website"). These Terms of Use (the "Terms"), along with our Privacy Policy, 
              govern your use of the Website and the content available therein.
            </p>

            <p className="mb-6">
              Please carefully read these Terms and the Privacy Policy, as they contain important information about your rights and responsibilities. 
              By accessing this Website you acknowledge that you have read, understood and agree with the Terms and the Privacy Policy. 
              If you do not wish to be bound by these Terms, please stop using this website immediately.
            </p>

            <h3 className="text-lg font-semibold mt-8 mb-4" style={{ color: themeColor }}>1. Acceptance of Terms</h3>
            <p className="mb-6">
              By accessing or using our Website, you agree to comply with and be bound by these Terms. We reserve the right to modify these 
              Terms at any time. Your continued use of the Website after changes are posted constitutes your acceptance of the modified Terms.
            </p>

            <h3 className="text-lg font-semibold mt-8 mb-4" style={{ color: themeColor }}>2. Use of the Website</h3>
            <p className="mb-4">You agree to use the Website only for lawful purposes. You must not:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Use the Website in any way that violates applicable laws or regulations</li>
              <li>Attempt to gain unauthorized access to any portion of the Website or its systems</li>
              <li>Transmit any harmful code, viruses, or malicious software</li>
              <li>Interfere with or disrupt the Website or servers connected to it</li>
              <li>Impersonate any person or entity or misrepresent your affiliation with any person or entity</li>
              <li>Use automated systems (bots, scrapers) to access the Website without our express written permission</li>
            </ul>

            <h3 className="text-lg font-semibold mt-8 mb-4" style={{ color: themeColor }}>3. Medical Services and Information</h3>
            <p className="mb-6">
              The information provided on this Website is for general informational purposes only and does not constitute medical advice, 
              diagnosis, or treatment. Always seek the advice of qualified healthcare providers with questions regarding medical conditions. 
              Never disregard professional medical advice or delay seeking it because of information you read on this Website.
            </p>

            <div className="bg-red-50 border border-red-200 rounded-lg p-5 mb-6">
              <p className="font-semibold mb-3" style={{ color: "#dc2626" }}>⚠️ Emergency Situations</p>
              <p>
                If you are experiencing a medical emergency, call 911 or go to the nearest emergency room immediately. 
                Do not use this Website for emergency medical needs.
              </p>
            </div>

            <h3 className="text-lg font-semibold mt-8 mb-4" style={{ color: themeColor }}>4. Virtual Care Services</h3>
            <p className="mb-4">Our Website may offer virtual care or telehealth services. By using these services, you acknowledge:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Virtual consultations have limitations compared to in-person examinations</li>
              <li>You are responsible for providing accurate and complete health information</li>
              <li>You have a reliable internet connection and appropriate device for virtual consultations</li>
              <li>You are located in British Columbia, Canada, during the virtual consultation</li>
              <li>Virtual care is not suitable for emergency situations</li>
              <li>Your healthcare provider may determine that an in-person visit is necessary</li>
            </ul>

            <h3 className="text-lg font-semibold mt-8 mb-4" style={{ color: themeColor }}>5. Services for Minors</h3>
            <p className="mb-6">
              Medical services for individuals under 16 years of age require the involvement and consent of a parent or legal guardian. 
              Minors aged 16-18 may consent to certain services but we may require parental involvement at our discretion or as required by law.
            </p>

            <h3 className="text-lg font-semibold mt-8 mb-4" style={{ color: themeColor }}>6. Account Security</h3>
            <p className="mb-6">
              If you create an account on our Website, you are responsible for maintaining the confidentiality of your account credentials 
              and for all activities that occur under your account. You agree to notify us immediately of any unauthorized access or use 
              of your account.
            </p>

            <h3 className="text-lg font-semibold mt-8 mb-4" style={{ color: themeColor }}>7. Intellectual Property</h3>
            <p className="mb-6">
              All content on this Website, including text, graphics, logos, images, and software, is the property of {clinicName} or 
              its content suppliers and is protected by Canadian and international copyright laws. You may not reproduce, distribute, 
              modify, or create derivative works without our express written permission.
            </p>

            <h3 className="text-lg font-semibold mt-8 mb-4" style={{ color: themeColor }}>8. Third-Party Links</h3>
            <p className="mb-6">
              Our Website may contain links to third-party websites or services. We are not responsible for the content, privacy policies, 
              or practices of these external sites. Your use of third-party websites is at your own risk.
            </p>

            <h3 className="text-lg font-semibold mt-8 mb-4" style={{ color: themeColor }}>9. Limitation of Liability</h3>
            <p className="mb-6">
              To the fullest extent permitted by law, {clinicName} and its affiliates, officers, directors, employees, and agents 
              shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related 
              to your use of the Website or services, even if we have been advised of the possibility of such damages.
            </p>

            <h3 className="text-lg font-semibold mt-8 mb-4" style={{ color: themeColor }}>10. Governing Law</h3>
            <p className="mb-6">
              These Terms shall be governed by and construed in accordance with the laws of the Province of British Columbia and 
              the federal laws of Canada applicable therein, without regard to conflict of law principles. Any disputes arising 
              from these Terms shall be subject to the exclusive jurisdiction of the courts of British Columbia.
            </p>

            <h3 className="text-lg font-semibold mt-8 mb-4" style={{ color: themeColor }}>11. Termination</h3>
            <p className="mb-6">
              We reserve the right to terminate or suspend your access to the Website at any time, without notice, for conduct 
              that we believe violates these Terms or is harmful to other users, us, or third parties, or for any other reason 
              at our sole discretion.
            </p>

            <h3 className="text-lg font-semibold mt-8 mb-4" style={{ color: themeColor }}>12. Contact Information</h3>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
              <p className="mb-3">If you have any questions about these Terms, please contact us:</p>
              <p className="font-semibold mb-2">{clinicName}</p>
              <p className="mb-1">350 Southeast Marine Drive, Vancouver, BC V5X 2S5</p>
              <p className="mb-1">Email: <a href={`mailto:${serviceEmail}`} className="text-sky-600 hover:underline">{serviceEmail}</a></p>
              <p>Phone: <a href="tel:604-323-0077" className="text-sky-600 hover:underline">604-323-0077</a></p>
            </div>

            <p className="mt-8 text-sm text-gray-600 italic">
              By using this Website, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use 
              and our Privacy Policy.
            </p>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-center">
            <button
              onClick={onClose}
              className="px-8 py-2 rounded-md text-white font-medium transition-colors duration-200 hover:opacity-90"
              style={{ backgroundColor: themeColor }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { TermsModal };
